import { createSlice, createAsyncThunk, createSelector } from "@reduxjs/toolkit";
import PayrollService from "../../services/payrollService";
import { handleApiError } from "../../api/handleApiError";

export const fetchEmployeesPayroll = createAsyncThunk(
  "payroll/fetchEmployeesPayroll",
  async ({ params, key }, thunkAPI) => {
    try {
      const res = await PayrollService.getEmployeesPayrollDetails(params);

      return {
        key,
        data: res.data?.data || [],
        totalRecords: res.data?.totalRecord || 0,
      };
    } catch (e) {
      return handleApiError(e, thunkAPI, "Payroll history fetch failed", key);
    }
  }
);

export const fetchPendingPayrollPayments = createAsyncThunk(
  "payroll/fetchPendingPayrollPayments",
  async ({ params, key }, thunkAPI) => {
    try {
      const res = await PayrollService.getPendingPayrollPayments(params);
      return {
        key,
        data: res.data?.data.items || [],
        totalRecords: res.data?.data?.totalPending || 0,
      };
    } catch (e) {
      return handleApiError(e, thunkAPI, "Failed to load pending payments", key);
    }
  }
);

export const fetchPayrollDashboard = createAsyncThunk(
  "payroll/fetchPayrollDashboard",
  async ({ params, key }, thunkAPI) => {
    try {
      const res = await PayrollService.payrollDashboard(params);

      return { key, data: res.data?.data || res.data || null, };
    } catch (e) {
      return handleApiError(e, thunkAPI, "Dashboard fetch failed", key);
    }
  }
);

export const runPayrollCalulation = createAsyncThunk(
  "payroll/runPayrollCalulation",
  async ({ params, key }, thunkAPI) => {
    try {
      const res = await PayrollService.runPayrollCalulation(params);
      return { key, data: res.data?.data || null };
    } catch (e) {
      return handleApiError(e, thunkAPI, "Run Payroll Failed", key);
    }
  }
);

export const generatePayCheck = createAsyncThunk(
  "payroll/generatePayCheck",
  async (payrollEmployeeId, { rejectWithValue }) => {
    try {
      if (!payrollEmployeeId) {
        return rejectWithValue("Invalid payroll employee id");
      }

      const res = await PayrollService.generatePayCheck(payrollEmployeeId);

      return { payrollEmployeeId, file: res.data, };
    } catch (e) {
      return rejectWithValue(e?.response?.data?.message || "Failed to generate paycheck");
    }
  }
);

export const exportPayrollExcel = createAsyncThunk(
  "payroll/exportPayrollExcel",
  async (headerId, { rejectWithValue }) => {
    try {
      if (!headerId) {
        return rejectWithValue("Invalid header id");
      }

      const res = await PayrollService.exportPayrollExcelAsync(headerId);

      return { headerId, file: res.data };
    } catch (e) {
      return rejectWithValue(e?.response?.data?.message || "Failed to export payroll Excel");
    }
  }
);

export const updateEmployeesWorkDetails = createAsyncThunk(
  "payroll/updateEmployeesWorkDetails",
  async ({ headerId, dto }, { rejectWithValue }) => {
    try {
      if (!headerId) {
        return rejectWithValue("Invalid header id");
      }

      const res = await PayrollService.updateEmployeesWorkDetails(headerId, dto);

      return res.data;
    } catch (e) {
      return rejectWithValue(e?.response?.data?.message || "Failed to update employee work details");
    }
  }
);

export const processPaymentDirectDeposit = createAsyncThunk(
  "payroll/processPaymentDirectDeposit",
  async ({ payrollHeaderId, key }, thunkAPI) => {
    try {
      if (!payrollHeaderId) {
        return thunkAPI.rejectWithValue("Invalid payroll header id");
      }

      const res = await PayrollService.paymentDirectDeposit(payrollHeaderId);

      // Check if the response indicates success
      if (res.data?.success === false || res.data?.error) {
        return thunkAPI.rejectWithValue({
          key,
          error: res.data?.message || "Failed to process direct deposit payment",
        });
      }

      return { payrollHeaderId, key, data: res.data };
    } catch (e) {
      return handleApiError(e, thunkAPI, "Failed to process direct deposit payment", key);
    }
  }
);

export const payTax = createAsyncThunk(
  "payroll/payTax",
  async ({ request, userId, key }, thunkAPI) => {
    try {
      const res = await PayrollService.payTax(request, userId);

      // Check if the response indicates success
      if (res.data?.status === false || res.data?.error) {
        return thunkAPI.rejectWithValue({
          key,
          error: res.data?.message || "Failed to pay tax",
        });
      }

      return { key, data: res.data };
    } catch (e) {
      return handleApiError(e, thunkAPI, "Failed to pay tax", key);
    }
  }
);

export const getTaxStatus = createAsyncThunk(
  "payroll/getTaxStatus",
  async ({ params, key }, thunkAPI) => {
    try {
      const res = await PayrollService.getTaxStatus(params);
      return { key, data: res.data };
    } catch (e) {
      return handleApiError(e, thunkAPI, "Failed to get tax status", key);
    }
  }
);

export const exportTaxCSV = createAsyncThunk(
  "payroll/exportTaxCSV",
  async (params, thunkAPI) => {
    try {
      const res = await PayrollService.exportTaxCSV(params);

      const blob = new Blob([res.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Tax_Status_${new Date().toISOString().slice(0, 19).replace(/:/g, "")}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return true;
    } catch (e) {
      return thunkAPI.rejectWithValue(e?.response?.data?.message || "Failed to export tax CSV");
    }
  }
);

const payrollSlice = createSlice({
  name: "payroll",

  initialState: {
    listDataByKey: {},
    totalByKey: {},
    listLoadingByKey: {},
    entityByKey: {},
    entityLoadingByKey: {},
    entityErrorByKey: {},
    generatingPaycheckId: null,
    exportingPayrollId: null,
    exportingTaxCSV: false,

    filters: {
      search: "",
      minPay: null,
      maxPay: null,
      status: "",
    },

    payrollState: {
      clientId: null,
      companies: [],
      clientName: "",
    },
  },

  reducers: {
    setPayrollFilters(state, action) {
      state.filters = {
        ...state.filters,
        ...action.payload,
      };
    },

    resetPayrollFilters(state) {
      state.filters = {
        search: "",
        minPay: null,
        maxPay: null,
        status: "",
      };
    },

    clearPayrollEntityError(state, action) {
      const key = action.payload;
      if (key) {
        state.entityErrorByKey[key] = null;
      }
    },

    setPayrollState(state, action) {
      state.payrollState = {
        ...state.payrollState,
        ...action.payload,
      };
    },
  },

  extraReducers: (builder) => {
    builder

      // =========================
      // PAYCHECK
      // =========================
      .addCase(generatePayCheck.pending, (state, action) => {
        state.generatingPaycheckId = action.meta.arg;
      })
      .addCase(generatePayCheck.fulfilled, (state) => {
        state.generatingPaycheckId = null;
      })
      .addCase(generatePayCheck.rejected, (state) => {
        state.generatingPaycheckId = null;
      })

      // =========================
      // EXPORT PAYROLL
      // =========================
      .addCase(exportPayrollExcel.pending, (state, action) => {
        state.exportingPayrollId = action.meta.arg;
      })
      .addCase(exportPayrollExcel.fulfilled, (state, action) => {
        state.exportingPayrollId = null;
        const { headerId, file } = action.payload;

        const blob = new Blob([file], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `Payroll_${headerId}_${new Date()
          .toISOString()
          .slice(0, 19)
          .replace(/:/g, "")}.xlsx`;
        a.click();
        window.URL.revokeObjectURL(url);
      })
      .addCase(exportPayrollExcel.rejected, (state) => {
        state.exportingPayrollId = null;
      })

      // =========================
      // EXPORT TAX CSV
      // =========================
      .addCase(exportTaxCSV.pending, (state) => {
        state.exportingTaxCSV = true;
      })
      .addCase(exportTaxCSV.fulfilled, (state) => {
        state.exportingTaxCSV = false;
      })
      .addCase(exportTaxCSV.rejected, (state) => {
        state.exportingTaxCSV = false;
      })

      // =========================
      // FETCH PAYROLL DASHBOARD (Explicit handler to ensure loading is reset)
      // =========================
      .addCase(fetchPayrollDashboard.pending, (state, action) => {
        const key = action.meta.arg?.key;
        if (key) {
          state.entityLoadingByKey[key] = true;
          state.entityErrorByKey[key] = null;
        }
      })
      .addCase(fetchPayrollDashboard.fulfilled, (state, action) => {
        const key = action.payload?.key;
        const data = action.payload?.data;
        if (key) {
          state.entityByKey[key] = data;
          state.entityLoadingByKey[key] = false;
        }
      })
      .addCase(fetchPayrollDashboard.rejected, (state, action) => {
        const key = action.meta.arg?.key;
        if (key) {
          state.entityLoadingByKey[key] = false;
          state.entityErrorByKey[key] = action.payload?.error || "Failed to fetch dashboard";
        }
      })

      // =========================
      // RUN PAYROLL
      // =========================
      .addCase(runPayrollCalulation.fulfilled, (state, action) => {
        const key = action.payload?.key;
        const data = action.payload?.data;
        if (key) {
          state.entityByKey[key] = data;
          state.entityLoadingByKey[key] = false;
        }
      })
      .addCase(runPayrollCalulation.rejected, (state, action) => {
        const key = action.meta.arg?.key;
        if (key) {
          state.entityLoadingByKey[key] = false;
        }
      })


      // =========================
      // 🔥 MATCHERS MUST COME LAST
      // =========================
      .addMatcher(
        (a) => a.type.endsWith("/pending") && a.meta.arg?.key,
        (state, action) => {
          const key = action.meta.arg.key;
          state.listLoadingByKey[key] = true;
          state.entityLoadingByKey[key] = true;
          state.entityErrorByKey[key] = null;
        }
      )

      .addMatcher(
        (a) => a.type.endsWith("/fulfilled") && a.payload?.key,
        (state, action) => {
          const { key, data, totalRecords } = action.payload;

          if (key.startsWith("runPayroll-")) return;

          const isDashboard = key === "payrollDashboard";

          if (Array.isArray(data) && !isDashboard) {
            state.listDataByKey[key] = data;
            state.totalByKey[key] = totalRecords || 0;
            state.listLoadingByKey[key] = false;
          } else {
            state.entityByKey[key] = data;
            state.entityLoadingByKey[key] = false;
            state.totalByKey[key] = totalRecords || 0;
          }
        }
      )

      .addMatcher(
        (a) => a.type.endsWith("/rejected") && a.payload?.key,
        (state, action) => {
          const { key, error } = action.payload;
          state.listLoadingByKey[key] = false;
          state.entityLoadingByKey[key] = false;
          state.entityErrorByKey[key] = error;
        }
      );
  }
});

export const {
  setPayrollFilters,
  resetPayrollFilters,
  clearPayrollEntityError,
  setPayrollState,
} = payrollSlice.actions;

// Memoized selectors
export const selectIsCalculatingPayroll = createSelector(
  (state) => state.payroll.entityLoadingByKey,
  (entityLoadingByKey) =>
    Object.keys(entityLoadingByKey).some((key) =>
      key.startsWith("runPayroll-") && entityLoadingByKey[key]
    )
);

export const selectUpdatingEmployeeIds = createSelector(
  (state) => state.payroll.entityLoadingByKey,
  (entityLoadingByKey) => {
    const updating = {};
    Object.keys(entityLoadingByKey).forEach((key) => {
      if (key.startsWith("update-workDetail") && entityLoadingByKey[key]) {
        const employeeId = key.replace("update-workDetail", "");
        updating[employeeId] = true;
      }
    });
    return { ...updating };
  }
);

export const selectLoadingMap = createSelector(
  (state) => state.payroll.entityLoadingByKey,
  (entityLoadingByKey) => ({ ...entityLoadingByKey })
);

export const selectPayrollState = createSelector(
  (state) => state.payroll.payrollState,
  (payrollState) => payrollState
);

export default payrollSlice.reducer;
